import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';
import type { Trade } from '../types';

export function useTrades() {
  return useQuery<Trade[]>({
    queryKey: ['trades'],
    queryFn: async () => {
      const { data } = await api.get('/trades');
      // backend returns { data: { trades: [...], count: N } }
      return data.data?.trades ?? data.data ?? [];
    },
  });
}

export function useCreateTrade() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: {
      targetUserId: string;
      offeredStickers: string[];
      requestedStickers: string[];
    }) => {
      const { data } = await api.post('/trades', payload);
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trades'] });
    },
  });
}

export function useTradeAction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      tradeId,
      action,
    }: {
      tradeId: string;
      action: 'accept' | 'reject' | 'cancel' | 'confirm';
    }) => {
      const { data } = await api.post(`/trades/${tradeId}/${action}`);
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trades'] });
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
    },
  });
}

export function useRateTrade() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      tradeId,
      score,
      comment,
    }: {
      tradeId: string;
      score: number;
      comment?: string;
    }) => {
      const { data } = await api.post(`/trades/${tradeId}/rate`, { score, comment });
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trades'] });
    },
  });
}
